import { string } from "prop-types";
import * as React from "react";
import { StylesConfig } from "react-select";
import { Option } from "shared/model/Option";
import colors from "shared/styles/colors";
import { ThemeType } from "shared/styles/theme";

export const selectHeight = "40px";
export const selectFontSize = "16px";

const selectLanguageModeHeight = "26px";
const selectLanguageModeFontSize = "14px";

export const selectStylesWithParameters = (props: ISelectProps & { theme: ThemeType } = {
    theme: ThemeType.light,
    height: selectHeight,
    width: "initial",
    isLanguageModeSelector: false,
    borderRadius: "25px",
    borderWidthLeft: "2px",
    borderWidthRight: "2px"
}): StylesConfig<Option, false> => ({
    control: (provided, { isMulti, getValue, isDisabled }) => ({
        ...provided,
        borderRadius: props.borderRadius,
        border: `2px solid ${getControlBorderColor(props.theme, isDisabled, props.hasError)}`,
        borderLeftWidth: props.borderWidthLeft,
        borderRightWidth: props.borderWidthRight,
        minHeight: props.isLanguageModeSelector ? selectLanguageModeHeight : selectHeight,
        // If a multiselect has more than 1 value let the container height grow automatically
        height: props.height ? props.height : isMulti && getValue().length > 1 ? "undefined" : props.isLanguageModeSelector ? selectLanguageModeHeight : selectHeight,
        minWidth: props.width,
        boxShadow: undefined,
        fontSize: props.isLanguageModeSelector ? selectLanguageModeFontSize : selectFontSize,
        color: props.theme === ThemeType.light ? colors.black : colors.white,
        backgroundColor: getControlBackgroundColors(props.theme, isDisabled, props.isLanguageModeSelector),
        "&:hover": {
            borderColor: colors.grey_2
        }
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "8px",
        marginTop: 0,
        marginBottom: 0,
        marginRight: !props.isLanguageModeSelector ? -16 : "initial",
        marginLeft: props.menuMarginLeft || 'initial',
        width: !props.isLanguageModeSelector ? "max-content" : "initial",
        minWidth: "100%",
        boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.1)",
        border: `1px solid ${colors.grey_2}`,
        color: props.theme === ThemeType.light ? colors.black : colors.white,
        backgroundColor: props.theme === ThemeType.light ? colors.white : colors.dark2
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    valueContainer: (provided, { isMulti, getValue }) => ({
        ...provided,
        padding: isMulti && getValue().length > 1 ? "2px" : "0px 4px 4px 14px"
    }),
    placeholder: (provided) => {
        return ({
            ...provided,
            fontSize: props.isLanguageModeSelector ? selectLanguageModeFontSize : selectFontSize,
            color: props.theme === ThemeType.light ? colors.black : colors.grey_4
        })
    },
    input: (provided) => ({
        ...provided,
        margin: '0px',
        paddingTop: '0px',
        color: props.theme === ThemeType.light ? colors.black : colors.white
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: props.isLanguageModeSelector ? "24px" : selectHeight
    }),
    container: (provided) => {
        return ({
            ...provided,
            overflow: "visible"
        })
    },
    group: (provided) => ({
        ...provided,
        paddingBottom: 4,
    }),
    groupHeading: (provided) => ({
        ...provided,
        letterSpacing: "2px",
        color: props.theme === ThemeType.light ? colors.grey_1 : colors.grey_2,
        fontFamily: "'Gilroy-Medium', 'medium-fallback'",
        margin: 0,
        padding: '6px 48px 12px 16px',
        fontSize: 12,
    }),
    option: (provided, { isDisabled, isFocused, isSelected }) => ({
        ...provided,
        padding: '8px 48px 8px 16px',
        fontSize: props.isLanguageModeSelector ? selectLanguageModeFontSize : selectFontSize,
        backgroundColor: isFocused
            ? props.theme === ThemeType.light ? colors.grey_6 : colors.dark
            : undefined,
        color: props.theme === ThemeType.light ?
            isSelected ? colors.purplePrimary : colors.black :
            isSelected ? colors.purpleTertiary : colors.white,
        ':active': {
            ...provided[':active'],
            backgroundColor: undefined
        },
        ...(props.isAgentGroupSelectorWithLinks && { padding: '0' }),
    }),
    multiValue: (base, state) => {
        const shared: React.CSSProperties = {
            borderRadius: "20px",
            height: "30px",
            alignItems: "center"
        }
        return state.data.isFixed ? {
            ...base,
            ...shared,
            backgroundColor: colors.grey_2,
            color: props.theme === ThemeType.light ? colors.black : colors.white
        } : {
            ...base,
            ...shared,
            backgroundColor: props.theme === ThemeType.light ? colors.pinkBackground : colors.warningDark2,
            color: props.theme === ThemeType.light ? colors.black : colors.white
        };
    },
    multiValueLabel: (base, state) => {
        const shared: React.CSSProperties = {
            color: props.theme === ThemeType.light ? colors.black : colors.white
        }
        return state.data.isFixed
            ? {
                ...base,
                ...shared,
                fontFamily: "'Gilroy-Bold', 'bold-fallback'",
                paddingRight: 6,
            }
            : {
                ...base,
                ...shared,
                padding: "2px",
                whiteSpace: "initial",
            };
    },
    multiValueRemove: (base, state) => {
        if (state.data.isFixed || state.isDisabled) {
            return ({
                ...base,
                display: 'none'
            })
        } else {
            return ({
                ...base,
                cursor: "pointer",
                borderRadius: "0px 25px 25px 0px",
                color: props.theme === ThemeType.light ? colors.pinkSecondary : colors.warningDark3,
                "&:hover": {
                    backgroundColor: props.theme === ThemeType.light ? colors.pinkBackground : colors.warningDark2,
                    color: props.theme === ThemeType.light ? colors.pinkPrimary : colors.grey_1
                }
            })
        }
    },
    indicatorSeparator: () => {
        return ({
            display: "none"
        })
    },
    singleValue: (provided) => {
        return ({
            ...provided,
            fontSize: props.isLanguageModeSelector ? selectLanguageModeFontSize : selectFontSize,
            color: props.theme === ThemeType.light ? colors.black : colors.white
        })
    },
    menuList: (provided) => {
        return ({
            ...provided,
            overflow: "overlay",
        })
    },
    dropdownIndicator: (provided, { isDisabled }) => ({
        ...provided,
        display: props.hideDropdownIndicator ? "none" : "initial",
        height: props.isLanguageModeSelector ? selectHeight : selectHeight,
        visibility: isDisabled ? "hidden" : "visible",
        color: props.theme === ThemeType.light ? colors.black : colors.white,
        '&:hover': {
            color: colors.grey_2
        }
    }),
    clearIndicator: (provided) => ({
        ...provided,
        color: props.theme === ThemeType.light ? colors.black : colors.white,
        '&:hover': {
            color: colors.grey_2
        }
    })
});

export const selectWithInput: StylesConfig<Option> = ({
    input: (provided) => ({
        ...provided,
        margin: '0px',
    }),
    valueContainer: (provided) => ({
        ...provided,
        paddingBottom: '3px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "40px",
    }),
})

const getControlBackgroundColors = (theme: ThemeType, isDisabled: boolean, isLanguageModeSelector?: boolean) => {
    if (theme === ThemeType.light) {
        if (isDisabled) {
            return colors.grey_2
        }
        if (isLanguageModeSelector) {
            return colors.grey_5;
        }
        return colors.white
    } else {
        if (isDisabled) {
            return colors.dark3
        }
        return colors.dark2
    }
}

const getControlBorderColor = (theme: ThemeType, isDisabled: boolean, hasError?: boolean) => {
    if (hasError) {
        return colors.warningDark
    } else if (isDisabled) {
        if (theme === ThemeType.light) {
            return colors.grey_5;
        } else {
            return colors.dark
        }
    } else {
        return colors.grey_2
    }
}

export interface ISelectProps {
    ref?: React.Ref<any>;
    height?: string;
    width?: string;
    isLanguageModeSelector?: boolean;
    isAgentGroupSelectorWithLinks?: boolean;
    hasError?: boolean,
    borderRadius?: string,
    borderWidthLeft?: string,
    borderWidthRight?: string,
    menuMarginLeft?: string,
    hideDropdownIndicator?: boolean;
}